// components/BarcodeScanner.tsx
import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { getErrorMessage } from '../utils/errors';

type DecodeControls = {
  stop: () => void;
};

export default function BarcodeScanner({
  onDetected,
  onFallback,
}: {
  onDetected: (s: string) => void;
  onFallback?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    let controls: DecodeControls | undefined;
    const videoEl = videoRef.current;

    void (async () => {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ video: true });
        for (const t of probe.getTracks()) t.stop();

        const reader = new BrowserMultiFormatReader();

        if (videoEl) {
          videoEl.muted = true;
          videoEl.playsInline = true;
          videoEl.autoplay = true;
        }

        const currentVideo = videoRef.current;
        if (!currentVideo) return;

        controls = await reader.decodeFromVideoDevice(
          undefined,
          currentVideo,
          (result, _err, ctrl) => {
            if (result && !stopped) {
              stopped = true;
              ctrl.stop();
              onDetected(result.getText().trim());
            }
          },
        );
      } catch (error_) {
        setError(getErrorMessage(error_, 'Camera not available'));
        setUnsupported(true);
        onFallback?.();
      }
    })();

    return () => {
      stopped = true;
      try {
        controls?.stop();
      } catch {
        /* ignore stop errors during cleanup */
      }
      const tracks = (videoEl?.srcObject as MediaStream | null)?.getTracks() ?? [];
      for (const t of tracks) t.stop();
    };
  }, [onDetected, onFallback]);

  if (unsupported) {
    return (
      <div
        className="p-4 text-sm"
        style={{
          background: 'rgba(244,168,142,0.15)',
          border: '1px solid var(--c-coral)',
          borderRadius: 'var(--r-md)',
          color: 'var(--c-charcoal)',
        }}
      >
        Camera unavailable. Please enter the barcode manually.
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      <video ref={videoRef} className="w-full h-full object-contain" muted playsInline autoPlay />
      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <div
          className="w-56 h-24"
          style={{
            border: '2px solid rgba(255,255,255,0.8)',
            borderRadius: 'var(--r-sm)',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
          }}
        />
      </div>
      {error && (
        <div
          className="absolute bottom-2 left-2 right-2 text-xs px-2 py-1"
          style={{
            background: 'var(--c-coral)',
            color: 'var(--c-charcoal)',
            borderRadius: 'var(--r-xs)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
